import { useEffect, useState } from "react";
import { getMexicanoSession, setupMexicano } from "../../../api/tournaments";
import { IMexicanoSession } from "../../../models/IAmericano";
import { StageDetails } from "../../../types/tournament";
import { formatRoundProgress } from "../../../utils/methods";
// The kit's own files, not the barrel: the barrel pulls in react-router-dom, which this step's test doesn't mock.
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { Field, FormGrid, Input } from "../../../ui/Field";
import { Alert } from "../../../ui/Feedback";

interface Props {
  stage: StageDetails;
  onSaved: () => void;
}

const parseCount = (value: string): number | null => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * Mexicano/Roulette "Create schedule" (spec 2026-09-29 §5, 2026-10-14 §3): only enrolls the
 * registered players and saves courts + planned rounds. No round is created here — organizers
 * check players in and start every round from the referee portal. Roulette's planned rounds are
 * optional: empty (0) plays until the organizer finishes.
 */
const MexicanoSetupStep = ({ stage, onSaved }: Props) => {
  const roundsOptional = stage.formatName === "Roulette";
  const savedRounds = stage.americanoRules?.roundsCount;
  const [courts, setCourts] = useState<string>(stage.americanoRules?.courts ? String(stage.americanoRules.courts) : "");
  const [rounds, setRounds] = useState<string>(
    roundsOptional ? (savedRounds && savedRounds > 0 ? String(savedRounds) : "") : String(savedRounds ?? 8)
  );
  const [session, setSession] = useState<IMexicanoSession | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMexicanoSession(stage.id)
      .then((res) => setSession(res.data))
      .catch(() => setSession(null));
  }, [stage.id]);

  const started = (session?.currentRound ?? 0) > 0;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // The api interceptor rejects with the response body, so the reason is error.message.
      await setupMexicano(stage.id, { courts: parseCount(courts), rounds: parseCount(rounds) ?? 0 });
      onSaved();
    } catch (err: any) {
      setError(err?.message || "Couldn't save the setup.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="americano-setup mexicano-setup"
      title={`${roundsOptional ? "Roulette" : "Mexicano"} Setup`}
      subtitle={
        <>
          Saving enrolls every registered player as "Not here" and creates no matches. On the day, mark who has arrived
          and start each round from the referee portal; late arrivals join from the next round.
        </>
      }
    >
      {started ? (
        <Alert tone="info" className="setup-summary">
          The event has started ({formatRoundProgress(session!.currentRound, session!.plannedRounds)}). Run it from
          the referee portal; to start over, delete the schedule from the stage page.
        </Alert>
      ) : (
        <>
          <FormGrid columns={3}>
            <Field label="Players">
              <p className="cs-static">
                {session === null
                  ? "…"
                  : session.players.length === 0
                    ? "Not set up yet"
                    : `${session.players.length} players enrolled`}
              </p>
            </Field>
            <Field label="Courts" htmlFor="mexicano-courts" hint="Matches at the same time each round">
              <Input
                id="mexicano-courts"
                type="number"
                min={1}
                placeholder="No limit"
                value={courts}
                onChange={(e) => setCourts(e.target.value)}
              />
            </Field>
            <Field
              label="Planned rounds"
              htmlFor="mexicano-rounds"
              hint={roundsOptional ? "Leave empty to play until you finish" : "The organizer can finish early from the portal"}
            >
              <Input
                id="mexicano-rounds"
                type="number"
                min={1}
                placeholder={roundsOptional ? "Until you finish" : undefined}
                value={rounds}
                onChange={(e) => setRounds(e.target.value)}
              />
            </Field>
          </FormGrid>
          {error && (
            <Alert tone="danger" className="setup-error">
              {error}
            </Alert>
          )}
          <div className="mexicano-setup-actions">
            <Button className="footer-btn next" onClick={save} loading={saving}>
              {saving ? "Saving..." : "Save setup"}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default MexicanoSetupStep;
